import { Controller } from "@hotwired/stimulus"

// Auto-save controller for forms
// Efficiently saves form data on field changes with debouncing
export default class extends Controller {
  static targets = ["status"]
  static values = { 
    url: String,
    delay: { type: Number, default: 1500 }, // 1.5 second delay
    method: { type: String, default: "PATCH" }
  }

  connect() {
    console.log("Auto-save controller connected")
    this.saveTimeout = null
    this.isInitialLoad = true
    this.originalFormData = new FormData(this.element)
    
    // Allow initial load to complete before enabling auto-save
    setTimeout(() => {
      this.isInitialLoad = false
    }, 1000)
  }

  disconnect() {
    if (this.saveTimeout) {
      clearTimeout(this.saveTimeout)
    }
  }

  // Main method called when any field changes (only on 'change' events now)
  fieldChanged(event) {
    // Skip if we're still in initial load
    if (this.isInitialLoad) return
    
    // Skip if this is a typeahead programmatic change
    if (event.target.dataset.skipAutosave === "true") return

    console.log("Field changed:", event.target.name, "->", event.target.value)
    
    // Clear existing timeout
    if (this.saveTimeout) {
      clearTimeout(this.saveTimeout)
    }

    // For currency/percentage fields, add extra delay to let formatting complete
    const isCurrencyField = this.isCurrencyOrPercentageField(event.target)
    const extraDelay = isCurrencyField ? 200 : 0

    // Show saving status immediately
    this.showStatus("saving")

    // Debounce the save operation with extra delay for currency fields
    this.saveTimeout = setTimeout(() => {
      this.performSave()
    }, this.delayValue + extraDelay)
  }

  // Helper method to identify currency/percentage fields
  isCurrencyOrPercentageField(element) {
    const fieldId = element.id
    const currencyFields = [
      'transaction_price',
      'transaction_concessions', 
      'transaction_commbasis',
      'transaction_commflat',
      'transaction_commcredit',
      'transaction_agent_charges'
    ]
    const percentageFields = [
      'transaction_commpct',
      'transaction_probability', 
      'transaction_referred_pct'
    ]
    
    return currencyFields.includes(fieldId) || percentageFields.includes(fieldId)
  }

  // Perform the actual save operation
  async performSave() {
    try {
      console.log("Performing auto-save...")
      
      const formData = new FormData(this.element)
      const url = this.urlValue || this.element.action

      const response = await fetch(url, {
        method: this.methodValue,
        body: formData,
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
        }
      })

      if (response.ok) {
        const result = await response.text()
        
        // Check if response contains validation errors
        if (result.includes('error_explanation') || result.includes('alert-danger')) {
          this.showStatus("error")
          console.log("Validation errors in auto-save response")
        } else {
          this.showStatus("saved")
          // Update original form data for future comparisons
          this.originalFormData = new FormData(this.element)
          console.log("Auto-save successful")
        }
      } else {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`)
      }
    } catch (error) {
      console.error("Auto-save failed:", error)
      this.showStatus("error")
    }
  }

  // Show status indicator
  showStatus(status) {
    // Try to find status element by target first, then by ID
    const statusElement = this.hasStatusTarget ? this.statusTarget : document.getElementById('auto-save-status')
    if (!statusElement) return
    statusElement.className = `auto-save-status ${status}`
    
    switch (status) {
      case "saving":
        statusElement.innerHTML = '<i class="fas fa-spinner fa-spin"></i> Saving...'
        break
      case "saved":
        statusElement.innerHTML = '<i class="fas fa-check text-success"></i> Saved'
        // Hide after 2 seconds
        setTimeout(() => {
          if (statusElement.className.includes('saved')) {
            statusElement.innerHTML = ''
            statusElement.className = 'auto-save-status'
          }
        }, 2000)
        break
      case "error":
        statusElement.innerHTML = '<i class="fas fa-exclamation-triangle text-danger"></i> Save failed'
        // Hide after 5 seconds
        setTimeout(() => {
          if (statusElement.className.includes('error')) {
            statusElement.innerHTML = ''
            statusElement.className = 'auto-save-status'
          }
        }, 5000)
        break
    }
  }

  // Manual save method that can be called by other controllers
  saveNow() {
    if (this.saveTimeout) {
      clearTimeout(this.saveTimeout)
    }
    this.performSave()
  }

  // Check if form has changes (utility method)
  hasChanges() {
    const currentFormData = new FormData(this.element)
    const currentEntries = Array.from(currentFormData.entries())
    const originalEntries = Array.from(this.originalFormData.entries())
    
    if (currentEntries.length !== originalEntries.length) return true
    
    return currentEntries.some(([key, value]) => {
      const originalValue = this.originalFormData.get(key)
      return originalValue !== value
    })
  }
}